import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import { fetchCategories, createCategory, deleteCategory } from '../api/contractorProfile';
import { Category } from '../types/contractor';

const AdminCategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    fetchCategories()
      .then(setCategories)
      .catch(() => setError('Failed to load categories.'))
      .finally(() => setLoading(false));
  }, []);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = newName.trim();
    if (!trimmed) return;
    setAdding(true);
    setAddError(null);
    try {
      const created = await createCategory(trimmed);
      setCategories(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setNewName('');
    } catch {
      setAddError('Failed to add category.');
    } finally {
      setAdding(false);
    }
  }

  async function handleDelete(id: number) {
    setDeletingId(id);
    try {
      await deleteCategory(id);
      setCategories(prev => prev.filter(c => c.id !== id));
    } catch {
      setError('Failed to delete category.');
    } finally {
      setDeletingId(null);
    }
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-surface-container-lowest font-sans antialiased">
      <TopAppBar />
      <main className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/admin" className="text-primary hover:underline text-sm">Admin</Link>
          <span className="text-outline">/</span>
          <h1 className="text-3xl font-bold text-on-surface">Categories</h1>
        </div>

        {error && <p className="text-error mb-4">{error}</p>}

        {/* Add category */}
        <div className="bg-surface rounded-2xl shadow-sm border border-outline-variant p-6 mb-6">
          <h2 className="text-base font-semibold text-on-surface mb-4">Add category</h2>
          <form onSubmit={handleAdd} className="flex gap-3">
            <input
              type="text"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="Category name"
              className="flex-1 border border-outline-variant rounded-lg px-3 py-2 text-sm bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={adding}
            />
            <button
              type="submit"
              disabled={adding || !newName.trim()}
              className="px-4 py-2 bg-primary text-on-primary rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {adding ? 'Adding...' : 'Add'}
            </button>
          </form>
          {addError && <p className="text-error text-sm mt-2">{addError}</p>}
        </div>

        {/* Category list */}
        <div className="bg-surface rounded-2xl shadow-sm border border-outline-variant p-6">
          <p className="text-on-surface-variant text-sm mb-4">{categories.length} categories</p>
          <ul className="divide-y divide-outline-variant">
            {categories.map(category => (
              <li key={category.id} className="flex items-center justify-between py-3">
                <span className="text-sm text-on-surface">{category.name}</span>
                <button
                  onClick={() => handleDelete(category.id)}
                  disabled={deletingId === category.id}
                  className="text-error text-sm hover:underline disabled:opacity-50"
                >
                  {deletingId === category.id ? 'Removing...' : 'Remove'}
                </button>
              </li>
            ))}
            {categories.length === 0 && (
              <li className="py-4 text-center text-on-surface-variant text-sm">No categories yet.</li>
            )}
          </ul>
        </div>
      </main>
      <BottomNavBar />
    </div>
  );
};

export default AdminCategoriesPage;
